// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import updateAccountSettingsFilterDefaults from "@mcmc/App/Handlers/Account/UpdateAccountSettingsFilterDefaults";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettingsFilterDefaults,
};

const mapStateToProps = (state: McMcApplicationState) => ({
    filterDefaults: state.xAccountSettingsFilterDefaults,
    editingAccount: state.pages.accountSettings.editingAccount,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsManageFilterDefaultsStyles {
    checkboxGroup?: CheckboxGroupComponentProps;
    checkbox?: CheckboxProps;
    title?: TypographyProps;
}

export const manageFilterDefaultsStyles: AccountSettingsManageFilterDefaultsStyles = {
    title: {
        variant: "h4",
        as: "h2",
    },
    checkbox: {
        typographyProps: {
            css: css`
                margin-left: 10px;
            `,
        },
    },
};

const styles = manageFilterDefaultsStyles;

const AccountSettingsManageFilterDefaults = ({
    filterDefaults,
    editingAccount,
    updateAccountSettingsFilterDefaults,
}: Props) => {
    if (!editingAccount || !filterDefaults) {
        return null;
    }

    const normallySotkcedChecked = filterDefaults.filtrationNormallyStockedProducts?.toLowerCase() === "true";
    return (
        <>
            <Typography {...styles.title}>{translate("Manage Filter Defaults")}</Typography>
            <CheckboxGroup {...styles.checkboxGroup}>
                <Checkbox
                    {...styles.checkbox}
                    onChange={(_, value) =>
                        updateAccountSettingsFilterDefaults({
                            filtrationNormallyStockedProducts: `${value}`,
                        })
                    }
                    checked={normallySotkcedChecked}
                    data-test-selector="accountSettings_normallyStockedFilter"
                >
                    {translate("Select Products Normally Stocked By Default")}
                </Checkbox>
            </CheckboxGroup>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsManageFilterDefaults),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "Manage Filter Defaults",
    },
};

export default widgetModule;
